import { href, Link } from 'react-router'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/about.ts'

export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'About Syrcha - Automated Privacy Compliance Platform',
		description:
			'Syrcha is a developer-friendly privacy compliance platform that scans websites for GDPR, CCPA, and AI Act issues and generates actionable, audit-ready reports.',
		matches,
	})

export default function AboutRoute() {
	return (
		// No `<main>` here: the marketing layout already owns the single main
		// landmark, and nesting a second one gave every page two.
		<>
			<Intro />
			<Principles />
			<Build />
			<Commitments />
			<EndCTA />
		</>
	)
}

function Intro() {
	return (
		<section className="relative isolate overflow-hidden bg-[var(--marketing-base)] pt-16 pb-20 sm:pt-24">
			<div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ about</p>
				<h1
					className="mt-4 max-w-3xl font-[family-name:var(--font-display)] text-[44px] leading-[1.02] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[68px]"
					style={{ textWrap: 'balance' }}
				>
					Compliance tooling for people who{' '}
					<span className="accent-underline">ship</span>.
				</h1>
				<div className="mt-10 grid gap-12 lg:grid-cols-[1.3fr_1fr]">
					<div className="space-y-5 text-[16px] leading-relaxed text-[var(--marketing-gray)]">
						<p>
							Syrcha exists because privacy compliance turned into a cottage
							industry of PDFs and consulting hours, while the actual scanner
							you can point at a URL still doesn't ship in most teams' CI.
						</p>
						<p>
							We build the scanner. Real browser, real consent flows, real
							requests captured. Findings include the fix. Reports are signed,
							versioned, and replayable. No call required.
						</p>
					</div>
					<dl className="grid grid-cols-2 gap-x-6 gap-y-5 border-t border-[var(--marketing-border)] pt-6 font-[family-name:var(--font-mono)] text-[12px]">
						{[
							{ k: 'Founded', v: '2025' },
							{ k: 'Hosted', v: 'EU only' },
							{ k: 'Frameworks', v: 'GDPR · CCPA · AI Act' },
							{ k: 'Trial', v: 'Free · no card' },
							{ k: 'Reports', v: 'Signed · SHA-256' },
							{ k: 'Source', v: 'Open ruleset' },
						].map((r) => (
							<div key={r.k}>
								<dt className="text-[var(--marketing-faint)]">{r.k}</dt>
								<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
									{r.v}
								</dd>
							</div>
						))}
					</dl>
				</div>

				<div className="mt-12 flex flex-wrap items-center gap-3">
					<Link to={href('/signup')} className="btn-marketing">
						Start free scan
						<span aria-hidden className="ml-2">
							→
						</span>
					</Link>
					<Link to={href('/support')} className="btn-marketing-outline">
						Get in touch
					</Link>
				</div>
			</div>
		</section>
	)
}

function Principles() {
	const items = [
		{
			n: '01',
			title: 'Findings include the fix',
			body: 'Every violation links to the offending request, the responsible script, and a worked remediation. Audit reports describe problems. We describe fixes.',
		},
		{
			n: '02',
			title: 'Reports are signed',
			body: 'Each report carries a SHA-256 of the page state and rule set used. Replayable on demand. Defensible in front of a regulator.',
		},
		{
			n: '03',
			title: 'We charge what is on the page',
			body: 'No "contact sales" tier. The Agency price is on the pricing page. There is a genuine free tier, no card needed to start.',
		},
		{
			n: '04',
			title: 'You can leave',
			body: 'Cancel from settings without sending an email. Export your reports. No retention games, no win-back calls.',
		},
	]
	return (
		<section className="relative border-t border-[var(--marketing-border)] bg-[var(--marketing-surface)] py-24">
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<header className="grid gap-8 md:grid-cols-[1.3fr_1fr] md:items-end">
					<div>
						<p className="eyebrow text-[var(--marketing-faint)]">
							/ principles
						</p>
						<h2
							className="mt-4 font-[family-name:var(--font-display)] text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
							style={{ textWrap: 'balance' }}
						>
							Four things we{' '}
							<span className="accent-underline">refuse to compromise</span>.
						</h2>
					</div>
					<p className="text-[15px] leading-relaxed text-[var(--marketing-gray)]">
						Stated up front so you can hold us to them later.
					</p>
				</header>

				<ol className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-[var(--marketing-border)] bg-[var(--marketing-border)] md:grid-cols-2">
					{items.map((it) => (
						<li
							key={it.n}
							className="group relative flex flex-col gap-6 bg-[var(--marketing-base)] p-8 transition-colors hover:bg-[var(--marketing-elevated)]"
						>
							<span className="font-[family-name:var(--font-mono)] text-[11px] tracking-[0.2em] text-[var(--marketing-accent)] uppercase">
								{it.n}
							</span>
							<div className="space-y-3">
								<h3 className="font-[family-name:var(--font-display)] text-[22px] font-semibold tracking-[-0.01em] text-[var(--marketing-black)]">
									{it.title}
								</h3>
								<p className="text-[14px] leading-relaxed text-[var(--marketing-gray)]">
									{it.body}
								</p>
							</div>
						</li>
					))}
				</ol>
			</div>
		</section>
	)
}

function Build() {
	const phases = [
		{
			tag: 'Q3 2025',
			title: 'First scans live',
			body: 'Hosted scanner online. First GDPR ruleset shipped. Add a domain, run a scan, get a result; the loop closed.',
		},
		{
			tag: 'Q4 2025',
			title: 'Reports and notifications',
			body: 'PDF + HTML report generator. Webhook + email notifications. Scheduled scans.',
		},
		{
			tag: 'Q1 2026',
			title: 'CCPA + signed reports',
			body: 'CCPA ruleset in beta. SHA-256 report stamping. Public beta opens.',
		},
		{
			tag: 'Q3 2026',
			title: 'AI Act ruleset',
			body: 'Transparency, chatbot disclosure, generated-content notice, risk-tier docs. EU AI Act go-live.',
		},
	]
	return (
		<section className="relative border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-24">
			<div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
				<header className="max-w-2xl">
					<p className="eyebrow text-[var(--marketing-faint)]">/ build log</p>
					<h2
						className="mt-4 font-[family-name:var(--font-display)] text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
						style={{ textWrap: 'balance' }}
					>
						What we've shipped. What's{' '}
						<span className="accent-underline">next</span>.
					</h2>
				</header>

				<ol className="relative mt-14 space-y-8">
					<span
						aria-hidden
						className="absolute top-2 bottom-2 left-[15px] w-px bg-[var(--marketing-border)]"
					/>
					{phases.map((p) => (
						<li key={p.tag} className="relative pl-12">
							<span
								aria-hidden
								className="absolute top-0 left-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--marketing-border-strong)] bg-[var(--marketing-base)] font-[family-name:var(--font-mono)] text-[10.5px] text-[var(--marketing-accent)]"
							>
								·
							</span>
							<p className="font-[family-name:var(--font-mono)] text-[11px] tracking-[0.18em] text-[var(--marketing-faint)] uppercase">
								{p.tag}
							</p>
							<h3 className="mt-1 font-[family-name:var(--font-display)] text-[20px] font-semibold tracking-[-0.01em] text-[var(--marketing-black)]">
								{p.title}
							</h3>
							<p className="mt-2 max-w-xl text-[14px] leading-relaxed text-[var(--marketing-gray)]">
								{p.body}
							</p>
						</li>
					))}
				</ol>
			</div>
		</section>
	)
}

function Commitments() {
	const items = [
		{
			title: 'Actionable guidance',
			body: 'Prioritised findings with concrete remediation, evidence, and the exact request or selector to look at.',
		},
		{
			title: 'Privacy and security',
			body: 'Minimal data processing, passkeys + 2FA, strict ownership checks, EU-only hosting. Public security disclosure path.',
		},
		{
			title: 'Reliable monitoring',
			body: 'Scheduled scans, transparent status page, diff between runs, and email alerts when something regresses.',
		},
		{
			title: 'Human support',
			body: 'No chatbots, no ticket purgatory. A real person replies, typically within one business day.',
		},
	]
	return (
		<section className="relative border-t border-[var(--marketing-border)] bg-[var(--marketing-surface)] py-24">
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<header className="grid gap-8 md:grid-cols-[1.3fr_1fr] md:items-end">
					<div>
						<p className="eyebrow text-[var(--marketing-faint)]">
							/ commitments
						</p>
						<h2
							className="mt-4 font-[family-name:var(--font-display)] text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
							style={{ textWrap: 'balance' }}
						>
							What you can <span className="accent-underline">hold us to</span>.
						</h2>
					</div>
					<p className="text-[15px] leading-relaxed text-[var(--marketing-gray)]">
						No buzzwords. If we miss one of these, call us on it.
					</p>
				</header>
				<ul className="mt-14 grid gap-4 md:grid-cols-2">
					{items.map((it) => (
						<li key={it.title} className="marketing-card p-7">
							<h3 className="font-[family-name:var(--font-display)] text-[20px] font-semibold tracking-[-0.01em] text-[var(--marketing-black)]">
								{it.title}
							</h3>
							<p className="mt-3 text-[14px] leading-relaxed text-[var(--marketing-gray)]">
								{it.body}
							</p>
						</li>
					))}
				</ul>
			</div>
		</section>
	)
}

function EndCTA() {
	return (
		// `isolate overflow-hidden` clips the 640px glow below: without it the
		// decoration pushed the document 125px wider than a 390px viewport.
		<section className="relative isolate overflow-hidden border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-24">
			<div
				aria-hidden
				className="pointer-events-none absolute top-1/2 left-1/2 h-[420px] w-[640px] -translate-x-1/2 -translate-y-1/2 opacity-60"
				style={{
					background:
						'radial-gradient(closest-side, rgba(103,232,249,0.10), rgba(103,232,249,0) 70%)',
				}}
			/>
			<div className="relative z-10 mx-auto max-w-3xl px-6 text-center lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ start</p>
				<h2
					className="mx-auto mt-4 max-w-2xl font-[family-name:var(--font-display)] text-[38px] leading-[1.04] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[56px]"
					style={{ textWrap: 'balance' }}
				>
					Point us at a URL. See for{' '}
					<span className="accent-underline">yourself</span>.
				</h2>
				<p className="mx-auto mt-6 max-w-xl text-[16px] leading-relaxed text-[var(--marketing-gray)]">
					Free first scan. No card. No call. You decide whether the report is
					worth more than the marketing.
				</p>
				<div className="mt-10 flex flex-wrap items-center justify-center gap-3">
					<Link to={href('/signup')} className="btn-marketing">
						Create account
						<span aria-hidden className="ml-2">
							→
						</span>
					</Link>
					<a href="/#pricing" className="btn-marketing-outline">
						See pricing
					</a>
				</div>
			</div>
		</section>
	)
}
